import { Copy, Eye, EyeOff, Key, User } from 'lucide-react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { STORAGE_KEYS } from '../../constants';
import { useToast } from '../../contexts/ToastContext';
import { useApiKeys } from '../../hooks/useApiKeys';
import { useUser } from '../../hooks/useUser';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
// import Switch from '../../components/ui/Switch';
import { cn } from '../../utils/cn';

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  sendingDomain: string;
  emailNotifications: boolean;
  campaignUpdates: boolean;
  weeklyReports: boolean;
  webhookUrl: string;
}

interface WorkspaceData {
  sendingDomain?: string;
  webhookUrl?: string;
  emailNotifications?: boolean;
  campaignUpdates?: boolean;
  weeklyReports?: boolean;
}

const Settings: React.FC = () => {
  const { user } = useUser();

  const { showToast } = useToast();
  const {
    apiKey,
    generateLoading,
    fetchLoading,
    generateError,
    generateSuccess,
    generateApiKey,
    clearState,
  } = useApiKeys();

  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    sendingDomain: '',
    emailNotifications: true,
    campaignUpdates: true,
    weeklyReports: false,
    webhookUrl: '',
  });

  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);

  // Load user data on component mount
  useEffect(() => {
    if (user) {
      const workspaceData: WorkspaceData = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.WORKSPACE) ?? '{}'
      );

      setFormData((prev) => ({
        ...prev,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        sendingDomain: workspaceData.sendingDomain || '',
        webhookUrl: workspaceData.webhookUrl || '',
        emailNotifications:
          workspaceData.emailNotifications !== undefined
            ? workspaceData.emailNotifications
            : true,
        campaignUpdates:
          workspaceData.campaignUpdates !== undefined
            ? workspaceData.campaignUpdates
            : true,
        weeklyReports:
          workspaceData.weeklyReports !== undefined
            ? workspaceData.weeklyReports
            : false,
      }));
    }
  }, [user]);

  // Handle API key success/error states
  useEffect(() => {
    if (generateSuccess) {
      showToast({
        type: 'success',
        title: 'API Key Generated',
        message: 'Your API key has been generated successfully!',
      });
      clearState();
    }
  }, [generateSuccess, showToast, clearState]);

  useEffect(() => {
    if (generateError) {
      showToast({
        type: 'error',
        title: 'Generation Failed',
        message: generateError,
      });
      clearState();
    }
  }, [generateError, showToast, clearState]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // const handleSavePreferences = async (): Promise<void> => {
  //   try {
  //     // Update workspace data in localStorage
  //     const currentWorkspace: WorkspaceData = JSON.parse(
  //       localStorage.getItem(STORAGE_KEYS.WORKSPACE) ?? '{}'
  //     );
  //     const updatedWorkspace: WorkspaceData = {
  //       ...currentWorkspace,
  //       emailNotifications: formData.emailNotifications,
  //       campaignUpdates: formData.campaignUpdates,
  //       weeklyReports: formData.weeklyReports,
  //     };
  //     localStorage.setItem(
  //       STORAGE_KEYS.WORKSPACE,
  //       JSON.stringify(updatedWorkspace)
  //     );

  //     // Here you would typically make an API call to update preferences
  //     // await updateNotificationPreferences(preferences);

  //     showToast({
  //       type: 'success',
  //       title: 'Preferences Saved',
  //       message: 'Your notification preferences have been saved successfully!',
  //     });
  //   } catch (error) {
  //     console.error('Error saving preferences:', error);
  //     showToast({
  //       type: 'error',
  //       title: 'Save Failed',
  //       message: 'Failed to save preferences. Please try again.',
  //     });
  //   }
  // };

  const handleCopyApiKey = (apiKey: string): void => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    showToast({
      type: 'success',
      title: 'Copied',
      message: 'API Key copied to clipboard!',
    });
  };

  const handleGenerateApiKey = (): void => {
    if (window.confirm('Are you sure you want to generate a new API key?')) {
      generateApiKey();
    }
  };

  // const handleVerifyDomain = async (): Promise<void> => {
  //   try {
  //     // Update workspace data in localStorage
  //     const currentWorkspace: WorkspaceData = JSON.parse(
  //       localStorage.getItem(STORAGE_KEYS.WORKSPACE) ?? '{}'
  //     );
  //     const updatedWorkspace: WorkspaceData = {
  //       ...currentWorkspace,
  //       sendingDomain: formData.sendingDomain,
  //       webhookUrl: formData.webhookUrl,
  //     };
  //     localStorage.setItem(
  //       STORAGE_KEYS.WORKSPACE,
  //       JSON.stringify(updatedWorkspace)
  //     );

  //     // Here you would typically make an API call to verify domain
  //     // await verifyDomain(formData.sendingDomain);

  //     showToast({
  //       type: 'info',
  //       title: 'Domain Verification',
  //       message: 'Domain verification has been initiated!',
  //     });
  //   } catch (error) {
  //     console.error('Error verifying domain:', error);
  //     showToast({
  //       type: 'error',
  //       title: 'Verification Failed',
  //       message: 'Failed to verify domain. Please try again.',
  //     });
  //   }
  // };

  // Show loading state while user data is being loaded
  if (!user) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div>
            <div className="h-8 bg-muted rounded w-32 mb-2"></div>
            <div className="h-4 bg-muted rounded w-72 max-w-full"></div>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-muted rounded-xl"></div>
                <div className="space-y-1">
                  <div className="h-5 bg-muted rounded w-20"></div>
                  <div className="h-3 bg-muted rounded w-40"></div>
                </div>
              </div>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-3 bg-muted rounded w-16"></div>
                    <div className="h-10 bg-muted rounded-lg"></div>
                  </div>
                ))}
                <div className="h-10 bg-muted rounded-lg mt-4"></div>
              </div>
            </div>
            <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted rounded-xl"></div>
                  <div className="h-5 bg-muted rounded w-24"></div>
                </div>
                <div className="h-9 bg-muted rounded-lg w-36"></div>
              </div>
              <div className="rounded-xl border border-border bg-muted/20 p-6 space-y-3">
                <div className="h-4 bg-muted rounded w-24"></div>
                <div className="h-11 bg-muted rounded-lg"></div>
                <div className="h-3 bg-muted rounded w-20"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="mb-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-1.5">
          Settings
        </h1>
        <p className="text-sm text-muted-foreground max-w-xl">
          Manage your profile, API keys, and workspace preferences.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Profile Section */}
        <div>
          <div className="bg-card rounded-2xl border border-border p-6 shadow-sm hover:shadow-md/5 transition-shadow duration-200">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">
                  Profile
                </h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Your name and email used across the account.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  type="text"
                  name="first_name"
                  label="First Name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  disabled={true}
                />
                <Input
                  type="text"
                  name="last_name"
                  label="Last Name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  disabled={true}
                />
              </div>
              <Input
                type="email"
                name="email"
                label="Email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="you@example.com"
                disabled={true}
              />
            </div>
          </div>
        </div>

        {/* Domain Section */}
        {/* <div className="lg:col-span-1">
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-success-background rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-success" />
              </div>
              <h2 className="text-base font-semibold text-foreground">
                Domain
              </h2>
            </div>

            <div className="space-y-4">
              <Input
                type="text"
                name="sendingDomain"
                label="Sending Domain"
                value={formData.sendingDomain}
                onChange={handleInputChange}
                placeholder="mail.yourdomain.com"
              />
              <div className="bg-warning-background border border-warning/50 rounded-lg p-3">
                <p className="text-sm text-warning-foreground">
                  Add DNS records to verify your domain
                </p>
              </div>
              <Button
                onClick={handleVerifyDomain}
                variant="outline"
                className="w-full"
              >
                Verify Domain
              </Button>
            </div>
          </div>
        </div> */}

        {/* Notifications Section */}
        {/* <div className="lg:col-span-1">
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-info-background rounded-lg flex items-center justify-center">
                <Bell className="w-5 h-5 text-info" />
              </div>
              <h2 className="text-base font-semibold text-foreground">
                Notifications
              </h2>
            </div>

            <div className="space-y-4">
              <Switch
                label="Email notifications"
                checked={formData.emailNotifications}
                onChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    emailNotifications: checked,
                  }))
                }
              />
              <Switch
                label="Campaign updates"
                checked={formData.campaignUpdates}
                onChange={(checked) =>
                  setFormData((prev) => ({ ...prev, campaignUpdates: checked }))
                }
              />
              <Switch
                label="Weekly reports"
                checked={formData.weeklyReports}
                onChange={(checked) =>
                  setFormData((prev) => ({ ...prev, weeklyReports: checked }))
                }
              />
              <Button
                onClick={handleSavePreferences}
                variant="primary"
                className="w-full"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        </div> */}

        {/* API Keys Section */}
        <div className="lg:col-span-1">
          <div className="bg-card rounded-2xl border border-border p-6 shadow-sm hover:shadow-md/5 transition-shadow duration-200 h-full flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-destructive/10 rounded-xl flex items-center justify-center shrink-0">
                  <Key className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground">
                    API Keys
                  </h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Use your API key to authenticate requests.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleGenerateApiKey}
                disabled={generateLoading}
                variant="primary"
                className="shrink-0"
              >
                {generateLoading ? 'Generating...' : 'Generate New Key'}
              </Button>
            </div>

            {fetchLoading ? (
              <div className="flex-1 flex flex-col items-center justify-center py-10 rounded-xl border border-border bg-muted/10">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                <p className="text-muted-foreground mt-3 text-sm">
                  Loading API key...
                </p>
              </div>
            ) : !apiKey ? (
              <div className="flex-1 flex flex-col items-center justify-center py-10 px-4 rounded-xl border border-dashed border-border bg-muted/10 text-center">
                <div className="w-12 h-12 rounded-xl bg-muted/50 flex items-center justify-center mb-4">
                  <Key className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">
                  No API key yet
                </p>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Generate an API key to connect external apps and integrations.
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">
                      API Key
                    </span>
                    <span className="px-2 py-1 text-xs rounded-full bg-success-background text-success border border-success/30">
                      Active
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="relative w-full sm:flex-1 min-w-0">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey.key}
                      readOnly
                      aria-label="API Key"
                      className="w-full pl-3 pr-10 py-2.5 border border-border rounded-xl bg-background font-mono text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                    <button
                      onClick={() => setShowApiKey(!showApiKey)}
                      type="button"
                      aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                      title={showApiKey ? 'Hide API key' : 'Show API key'}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground rounded-r-xl transition-colors"
                    >
                      {showApiKey ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <Button
                    onClick={() => handleCopyApiKey(apiKey.key)}
                    variant="outline"
                    size="small"
                    className={cn(
                      'flex items-center gap-2 shrink-0',
                      copied && 'bg-success/10 border-success text-success'
                    )}
                  >
                    <Copy className="w-4 h-4 shrink-0" />
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>

                <div className="mt-2 text-xs text-muted-foreground">
                  Created:{' '}
                  {apiKey.createdAt
                    ? new Date(apiKey.createdAt).toLocaleDateString()
                    : 'N/A'}
                </div>
              </div>
            )}

            {/* <div className="mt-6">
          <Input
            type="url"
            name="webhookUrl"
            label="Webhook URL"
            value={formData.webhookUrl}
            onChange={handleInputChange}
            placeholder="https://yourdomain.com/webhook"
          />
        </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
